const EGD_GAME_LIST_PREFIX = "egd/game-list/";

/** @param {unknown} value */
const sanitizeImagePath = (value) =>
  typeof value === "string" ? value.trim().replace(/^\/+/, "") : "";

/** @param {unknown} value */
const imagePathFromLocaleMap = (value, locale = "en") => {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const map = value;
    const localized = map[locale] ?? map.en ?? map.fr;
    if (typeof localized === "string" && localized.trim()) return localized.trim();
  }
  return "";
};

/** @param {unknown} value */
const imagePathFromUrl = (value) => {
  if (typeof value !== "string" || !value.trim()) return "";
  const trimmed = value.trim();

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const { pathname } = new URL(trimmed);
      return pathname
        .replace(/^\/+/, "")
        .replace(/^pgfe\/img\//i, "")
        .replace(/^fit-in\/[^/]+\//i, "");
    } catch {
      return "";
    }
  }

  return trimmed
    .replace(/^\/+/, "")
    .replace(/^pgfe\/img\//i, "")
    .replace(/^fit-in\/[^/]+\//i, "");
};

/** @param {Record<string, unknown>} doc @param {'en' | 'fr'} locale */
const resolveEgdGameImageFromDoc = (doc, locale = "en") => {
  const rawImage = sanitizeImagePath(imagePathFromLocaleMap(doc.image, locale));
  const fromImage = rawImage.includes("/") ? rawImage : normalizeEgdGameImage(rawImage);
  const hasDirectory = (value) => typeof value === "string" && value.includes("/");

  let fromMedia = "";
  const media = doc.imageMedia;
  if (media && typeof media === "object" && !Array.isArray(media)) {
    if (typeof media.url === "string" && media.url.trim()) {
      const fromUrl = imagePathFromUrl(media.url);
      if (fromUrl) fromMedia = fromUrl;
    }

    if (!fromMedia && typeof media.filename === "string" && media.filename.trim()) {
      const filename = sanitizeImagePath(media.filename);
      const prefix = sanitizeImagePath(media.prefix || media.folder || media.directory || "");
      fromMedia = prefix ? `${prefix}/${filename}` : filename;
    }
  }

  // If `image` is only a filename but media points to a full path,
  // prefer the media path so non-EGD directory assets render correctly.
  if (fromMedia && hasDirectory(fromMedia) && (!fromImage || !hasDirectory(fromImage))) {
    return fromMedia;
  }

  if (fromImage) return fromImage;
  if (fromMedia) return fromMedia;

  return "";
};

/**
 * @typedef {Object} EgdGameCard
 * @property {string|number} [id] - Payload doc id; present when mapped from a relation resolved via `depth`, used to join with jackpot meters
 * @property {string} game_name
 * @property {string} status - Legacy values: `new`, `active`, `CNY`, `archived`
 * @property {string} manufacturer
 * @property {string|number} min_bet
 * @property {string} jackpot_min_bet
 * @property {string} title_en
 * @property {string} text_en
 * @property {string} title_fr
 * @property {string} text_fr
 * @property {string} image - Relative asset path (either a filename or a path like `egd/game-list/...` or `promotions/...`).
 */

/** @param {unknown} value */
export function normalizeEgdGameImage(value) {
  if (value == null || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (trimmed.startsWith(EGD_GAME_LIST_PREFIX)) {
    return trimmed.slice(EGD_GAME_LIST_PREFIX.length);
  }
  return trimmed;
}

/** @param {unknown} status */
const mapGameStatus = (status) => {
  if (status === "cny") return "CNY";
  return typeof status === "string" ? status : "active";
};

/**
 * Maps a Payload `egd-games` document to the legacy ERS card shape
 * consumed by GameListCard and gameListUtils.
 *
 * @param {Record<string, unknown>} doc
 * @param {'en' | 'fr'} [locale]
 * @returns {EgdGameCard}
 */
export function mapEgdGame(doc, locale = "en") {
  const safeDoc =
    doc && typeof doc === "object" && !Array.isArray(doc) ? doc : {};

  const title = typeof safeDoc.title === "string" ? safeDoc.title : "";
  const text = typeof safeDoc.text === "string" ? safeDoc.text : "";

  return {
    id:
      typeof safeDoc.id === "string" || typeof safeDoc.id === "number"
        ? safeDoc.id
        : undefined,
    game_name: typeof safeDoc.gameName === "string" ? safeDoc.gameName : "",
    status: mapGameStatus(safeDoc.gameStatus ?? safeDoc.status),
    manufacturer:
      typeof safeDoc.manufacturer === "string" ? safeDoc.manufacturer : "",
    min_bet:
      typeof safeDoc.minBet === "string" || typeof safeDoc.minBet === "number"
        ? safeDoc.minBet
        : "",
    jackpot_min_bet:
      typeof safeDoc.jackpotMinBet === "string" ||
      typeof safeDoc.jackpotMinBet === "number"
        ? safeDoc.jackpotMinBet
        : "",
    title_en: locale === "en" ? title : "",
    text_en: locale === "en" ? text : "",
    title_fr: locale === "fr" ? title : "",
    text_fr: locale === "fr" ? text : "",
    image: resolveEgdGameImageFromDoc(safeDoc, locale),
  };
}

/**
 * @param {{ docs?: unknown[] }} payloadResponse
 * @param {'en' | 'fr'} [locale]
 * @returns {EgdGameCard[]}
 */
export function mapEgdGamesResponse(payloadResponse, locale = "en") {
  const docs = Array.isArray(payloadResponse?.docs) ? payloadResponse.docs : [];
  return docs.map((doc) => mapEgdGame(doc, locale));
}
